/**
 * bioThemeCache — module-level singleton that caches page_settings + profile
 * per username for the duration of the browser session.
 *
 * Used to:
 *  1. Render themed loading-state backgrounds (instead of white) when navigating
 *     between bio sub-pages (Store, Contact, Chat).
 *  2. Feed instant-background data to pages before their async fetch resolves.
 */

import type { PageSettings } from '@/types/editor';
import { getBackgroundStyle, getPresetAnimationClass } from '@/lib/background';

export type BioThemeEntry = {
  settings: PageSettings;
  profile: {
    username: string;
    display_name: string | null;
    bio: string | null;
    avatar_url: string | null;
  };
};

const _cache = new Map<string, BioThemeEntry>();
let _lastEntry: BioThemeEntry | null = null;

export const getBioTheme = (username: string): BioThemeEntry | null =>
  _cache.get(username.toLowerCase()) ?? null;

export const getLastBioTheme = (): BioThemeEntry | null => _lastEntry;

export const setBioTheme = (username: string, entry: BioThemeEntry): void => {
  _cache.set(username.toLowerCase(), entry);
  _lastEntry = entry;
};

/**
 * Returns a CSS background suitable for a lightweight loading state.
 * Animated / video backgrounds cannot be replicated, so they fall back to the
 * stored `background_color` — still far better than a solid white flash.
 */
export function getInstantBg(settings: PageSettings): { background: string; fontFamily?: string } {
  const preset = getPresetAnimationClass(settings);
  const isVideo = settings.background_mode === 'custom' && !!settings.background_video_url;

  if (preset || isVideo) {
    return {
      background: settings.background_color || '#0d0d0d',
      fontFamily: settings.font_family,
    };
  }

  return {
    background: getBackgroundStyle(settings) ?? '#ffffff',
    fontFamily: settings.font_family,
  };
}
